import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoginModal } from '../components/Auth/LoginModel';
import { LoginForm } from '../components/Auth/LoginForm';
import LanguageSelector from '../components/LanguageSelector';
import logo from '../img/logo.png';
import { useLanguage } from '../hooks/useLanguage';

import {
    Car,
    Wrench,
    MessageSquare,
    Clock,
    Shield,
    Menu,
    X,
    ChevronRight,
    Smartphone,
    Store,
    Scan,
    Link2,
    Wrench as RepairIcon,
    Settings,
    Gift,
    Zap
} from 'lucide-react';
import homeImage from '../img/inspecly.png';

interface Feature {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const LandingPage: React.FC = () => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    const features: Feature[] = [
        {
            icon: <Scan className="w-6 h-6" />,
            title: t('features.items.scan.title'),
            description: t('features.items.scan.description')
        },
        {
            icon: <Link2 className="w-6 h-6" />,
            title: t('features.items.connect.title'),
            description: t('features.items.connect.description')
        },
        {
            icon: <RepairIcon className="w-6 h-6" />,
            title: t('features.items.repair.title'),
            description: t('features.items.repair.description')
        },
        {
            icon: <MessageSquare className="w-6 h-6" />,
            title: t('features.items.tracking.title'),
            description: t('features.items.tracking.description')
        }
    ];

    const handleCloseLoginModal = (): void => {
        setIsLoginModalOpen(false);
    };

    const handleMenuToggle = (): void => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();
    };

    return (
        <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white ${isRTL ? 'rtl' : ''}`}>
            {/* Navigation */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <div className="flex-shrink-0 flex items-center">
                            <img src={logo} alt="Hirrif Logo" width={80} height={80} />
                        </div>

                        {/* Menu desktop */}
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#comment-ca-marche" className="text-gray-600 hover:text-[#115DF2]">
                                {t('nav.howItWorks')}
                            </a>
                            <a href="#services" className="text-gray-600 hover:text-[#115DF2]">
                                {t('nav.services')}
                            </a>
                            <a href="#tarifs" className="text-gray-600 hover:text-[#115DF2]">
                                {t('nav.pricing')}
                            </a>
                            <Link to="/contact" className="text-gray-600 hover:text-[#115DF2]">
                                {t('nav.contact')}
                            </Link>
                            <LanguageSelector />
                            <button
                                onClick={() => setIsLoginModalOpen(true)}
                                className="text-[#115DF2] hover:text-[#0E4BC2] font-medium"
                                type="button"
                            >
                                {t('nav.login')}
                            </button>
                        </div>

                        {/* Menu mobile burger */}
                        <div className="md:hidden">
                            <button
                                onClick={handleMenuToggle}
                                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                                type="button"
                                aria-label="Menu"
                            >
                                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                            </button>
                        </div>
                    </div>

                    {/* Menu mobile panel */}
                    {isMenuOpen && (
                        <div className="md:hidden bg-white border-t">
                            <div className="px-2 pt-2 pb-3 space-y-1">
                                <a
                                    href="#comment-ca-marche"
                                    className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-[rgba(17,93,242,0.05)] hover:text-[#115DF2]"
                                >
                                    {t('nav.howItWorks')}
                                </a>
                                <a
                                    href="#services"
                                    className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-[rgba(17,93,242,0.05)] hover:text-[#115DF2]"
                                >
                                    {t('nav.services')}
                                </a>
                                <a
                                    href="#tarifs"
                                    className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-[rgba(17,93,242,0.05)] hover:text-[#115DF2]"
                                >
                                    {t('nav.pricing')}
                                </a>
                                <Link
                                    to="/contact"
                                    className="block px-3 py-2 rounded-lg text-gray-600 hover:bg-[rgba(17,93,242,0.05)] hover:text-[#115DF2]"
                                >
                                    {t('nav.contact')}
                                </Link>
                                <div className="px-3 py-2">
                                    <LanguageSelector />
                                </div>
                                <button
                                    onClick={() => setIsLoginModalOpen(true)}
                                    className="block w-full text-left px-3 py-2 rounded-lg text-[#115DF2] hover:bg-[rgba(17,93,242,0.05)]"
                                    type="button"
                                >
                                    {t('nav.login')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative pt-16 pb-32">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:grid lg:grid-cols-2 lg:gap-8">
                        <div>
                            <div className="mb-8">
                                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl">
                                    <span className="block">{t('hero.title.line1')}</span>
                                    <span className="block text-[#115DF2]">{t('hero.title.line2')}</span>
                                </h1>
                                <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl">
                                    {t('hero.subtitle')}
                                </p>
                                <div className="mt-8 space-x-4">
                                    <Link
                                        to="/register"
                                        state={{
                                            subscription: "COMMISSION",
                                            fromPricing: true
                                        }}
                                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#115DF2] hover:bg-[#0E4BC2] transition-colors"
                                    >
                                        {t('pricing.plan.cta.early')}
                                        <Store className={`${isRTL ? 'mr-2' : 'ml-2'} h-5 w-5`} />
                                    </Link>
                                </div>

                                {/* Free Commission Badge */}
                            {/*    <div className="mt-6 flex items-center text-sm">
                                    <Zap className="w-5 h-5 text-green-500 mr-1" />
                                    <span className="font-semibold text-green-600">
                      {t('register.freeOffer.title')} • {t('pricing.commission.description', { percentage: '5' })}
                    </span>
                                </div>*/}
                            </div>

                            {/* Apps Section */}
                            <div className="mt-8 p-6 bg-[rgba(17,93,242,0.05)] rounded-lg">
                                <h2 className="text-xl font-semibold mb-3">{t('hero.apps.title')}</h2>
                                <p className="text-gray-600 mb-4">
                                    {t('hero.apps.subtitle')}
                                </p>

                                <div className="flex flex-wrap gap-4">
                                    <div className="relative group">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-[#115DF2] to-[#3678FF] rounded-lg blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
                                        <a
                                            href="https://apps.apple.com/us/app/inspecly/id6755496831"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="relative flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg transition-all duration-300 transform group-hover:scale-105"
                                        >
                                            <svg viewBox="0 0 512 512" className="w-6 h-6 mr-2">
                                                <path fill="currentColor" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                                            </svg>
                                            App Store

                                        </a>
                                    </div>

                                    <div className="relative group">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-[#115DF2] to-[#3678FF] rounded-lg blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
                                        <a
                                            href="https://play.google.com/store/apps/details?id=com.inspecly.mobile"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="relative flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg transition-all duration-300 transform group-hover:scale-105"
                                        >
                                            <svg viewBox="0 0 512 512" className="w-6 h-6 mr-2">
                                                <path fill="currentColor" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                                            </svg>
                                            Play Store
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 lg:mt-0">
                            <div className="relative">
                                <div className="aspect-w-16 aspect-h-9 w-full">
                                    <iframe
                                        className="w-full h-[300px] lg:h-[400px] rounded-lg shadow-lg"
                                        src="https://www.youtube.com/embed/DuVjlTCSdQU"
                                        title="Présentation Inspecly"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div id="services" className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-extrabold text-center mb-12">
                        {t('features.title')}
                    </h2>
                    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {features.map((feature: Feature, index: number) => (
                            <div
                                key={index}
                                className="p-6 bg-gradient-to-b from-[rgba(17,93,242,0.05)] to-white rounded-lg border border-[rgba(17,93,242,0.2)] hover:shadow-lg transition-shadow"
                            >
                                <div className="w-12 h-12 bg-[rgba(17,93,242,0.1)] rounded-lg flex items-center justify-center text-[#115DF2] mb-4">
                                    {feature.icon}
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Pricing Section */}
            <div id="tarifs" className="py-16 bg-gradient-to-b from-[rgba(17,93,242,0.05)] to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-extrabold">
                            {t('pricing.title')}
                        </h2>
                        <p className="mt-4 text-xl text-gray-600">
                            {t('pricing.subtitle')}
                        </p>
                    </div>

                    {/* Pricing Card */}
                    <div className="max-w-md mx-auto">
                        <div className="rounded-lg shadow-lg bg-white overflow-hidden border-2 border-green-500 transform transition-transform hover:scale-105">
                            <div className="p-6">
                <span className="inline-flex px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full mb-3">
                  {t('register.freeOffer.title')}
                </span>
                                <h3 className="text-2xl font-bold text-gray-900">{t('pricing.plan.name')}</h3>
                                <div className="mt-4 flex items-center">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {t('pricing.plan.price')}
                    </span>
                                    <span className="text-gray-500 ml-1">
                      {t('pricing.plan.perMonth')}
                    </span>
                                </div>
                                {/*<div className="mt-1 text-sm text-green-600 font-medium">
                                    <p>{t('pricing.commission.description', { percentage: '5' })}</p>
                                </div>*/}
                                <p className="mt-2 text-gray-500">
                                    {t('pricing.plan.description')}
                                </p>
                                <div className="mt-6">
                                    <Link
                                        to="/register"
                                        state={{
                                            subscription: "COMMISSION",
                                            fromPricing: true
                                        }}
                                        className="block w-full bg-[#115DF2] text-white text-center px-4 py-2 rounded-lg hover:bg-[#0E4BC2] transition-colors"
                                    >
                                        {t('pricing.plan.cta.early')}
                                    </Link>
                                </div>
                            </div>
                            <div className="px-6 pb-6">
                                <div className="border-t border-gray-200 pt-6">
                                    <ul className="space-y-3">
                                        <li className="flex items-start">
                                            <div className="h-5 w-5 text-green-500 mt-1 flex-shrink-0">✓</div>
                                            <span className="ml-3 text-gray-600">
                        {t('pricing.plan.features.noSetupFee')}
                      </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="h-5 w-5 text-green-500 mt-1 flex-shrink-0">✓</div>
                                            <span className="ml-3 text-gray-600">
                        {t('pricing.plan.features.unlimitedInspections')}
                      </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="h-5 w-5 text-green-500 mt-1 flex-shrink-0">✓</div>
                                            <span className="ml-3 text-gray-600">
                        {t('pricing.plan.features.inspectionManagement')}
                      </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="h-5 w-5 text-green-500 mt-1 flex-shrink-0">✓</div>
                                            <span className="ml-3 text-gray-600">
                        {t('pricing.plan.features.clientChat')}
                      </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="h-5 w-5 text-green-500 mt-1 flex-shrink-0">✓</div>
                                            <span className="ml-3 text-gray-600">
                        {t('pricing.plan.features.quoteManagement')}
                      </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="h-5 w-5 text-green-500 mt-1 flex-shrink-0">✓</div>
                                            <span className="ml-3 text-gray-600">
                        {t('pricing.plan.features.aiDiagnostic')}
                      </span>
                                        </li>
                              {/*          <li className="flex items-start">
                                            <div className="h-5 w-5 text-green-500 mt-1 flex-shrink-0">✓</div>
                                            <span className="ml-3 text-gray-600">
                        {t('pricing.plan.features.commissionBased')}
                      </span>
                                        </li>*/}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                   {/* <div className="mt-8 text-center max-w-2xl mx-auto">
                        <p className="text-gray-600">
                            {t('pricing.commission.noMonthlyFee')} • {t('pricing.commission.payOnSuccess')}
                        </p>
                    </div>*/}
                </div>
            </div>

            {/* How It Works Section */}
            <div id="comment-ca-marche" className="py-16 bg-gradient-to-b from-white to-[rgba(17,93,242,0.05)]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-extrabold text-center mb-12">
                        {t('howItWorks.title')}
                    </h2>

                    {/* Steps */}
                    <div className="max-w-4xl mx-auto mb-16">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-[rgba(17,93,242,0.1)] rounded-full flex items-center justify-center text-[#115DF2] mx-auto mb-4">
                                    <Scan className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">{t('howItWorks.steps.scan.title')}</h3>
                                <p className="text-gray-600">{t('howItWorks.steps.scan.description')}</p>
                            </div>

                            <div className="text-center">
                                <div className="w-16 h-16 bg-[rgba(17,93,242,0.1)] rounded-full flex items-center justify-center text-[#115DF2] mx-auto mb-4">
                                    <Link2 className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">{t('howItWorks.steps.connect.title')}</h3>
                                <p className="text-gray-600">{t('howItWorks.steps.connect.description')}</p>
                            </div>

                            <div className="text-center">
                                <div className="w-16 h-16 bg-[rgba(17,93,242,0.1)] rounded-full flex items-center justify-center text-[#115DF2] mx-auto mb-4">
                                    <RepairIcon className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">{t('howItWorks.steps.repair.title')}</h3>
                                <p className="text-gray-600">{t('howItWorks.steps.repair.description')}</p>
                            </div>
                        </div>
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <img
                            src={homeImage}
                            alt="Interface Inspecly pour garagistes"
                            className="w-full rounded-lg shadow-lg"
                        />
                    </div>
                </div>
            </div>

            {/* Contact Section */}
            <div id="contact" className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl font-extrabold mb-8">
                            {t('contact.title')}
                        </h2>
                        <p className="text-gray-600 mb-8">
                            {t('contact.description')}
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#115DF2] hover:bg-[#0E4BC2] transition-colors"
                        >
                            {t('contact.cta')}
                        </Link>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-[#0A337F] text-white">
                <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="md:col-span-1">
                            <div className="flex items-center">
                                <Shield className="h-8 w-8" />
                                <span className="ml-2 text-2xl font-bold">
                  Inspecly<span className="text-[#9DB5F3] font-normal">.io</span>
                </span>
                            </div>
                            <p className="mt-4 text-[#C5D3F9]">
                                {t('footer.description')}
                            </p>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
                            <ul className="space-y-2">
                                <li>
                                    <Link to="/#comment-ca-marche" className="text-[#C5D3F9] hover:text-white">
                                        {t('nav.howItWorks')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/#services" className="text-[#C5D3F9] hover:text-white">
                                        {t('nav.services')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/#tarifs" className="text-[#C5D3F9] hover:text-white">
                                        {t('nav.pricing')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/contact" className="text-[#C5D3F9] hover:text-white">
                                        {t('nav.contact')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t('footer.legal')}</h3>
                            <ul className="space-y-2">
                                <li>
                                    <Link to="/cgu" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.terms')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/cgv" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.sales')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/privacy-policy" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.privacy')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/cookie-policy" className="text-[#C5D3F9] hover:text-white">
                                        {t('footer.cookies')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-4">{t('footer.contact')}</h3>
                            <p className="text-[#C5D3F9]">
                                {t('footer.email')}<br />
                                {t('footer.address')}
                            </p>
                            <div className="mt-4 flex space-x-4">
                                <a
                                    href="https://apps.apple.com/us/app/inspecly/id6755496831"
                                    className="text-[#C5D3F9] hover:text-white"
                                    title="Télécharger sur App Store"
                                >
                                    <svg viewBox="0 0 384 512" className="w-6 h-6">
                                        <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                                    </svg>
                                </a>
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.inspecly.mobile"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#C5D3F9] hover:text-white"
                                    title="Télécharger sur Google Play"
                                >
                                    <svg viewBox="0 0 512 512" className="w-6 h-6">
                                        <path fill="currentColor" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 pt-8 border-t border-[#1A4395] text-center text-[#C5D3F9]">
                        <p>&copy; {new Date().getFullYear()} BIYA IT SAS. {t('footer.copyright')}</p>
                    </div>
                </div>
            </footer>

            <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseLoginModal}>
                <LoginForm onClose={handleCloseLoginModal} isModal={true} />
            </LoginModal>
        </div>
    );
};

export default LandingPage;